<script>
import { reactive, ref, watch } from 'vue'
import { setOverviewView } from '@/stores/View'

import __ from '@/libraries/Translation'

import Alert from '@/components/Alert'
import Banks from '@/components/Banks'
import Button from '@/components/Button'
import TextField from '@/components/Form/TextField'
import PhoneField from '@/components/Form/PhoneField'
import BooleanGroupField from '@/components/Form/BooleanGroupField'
import BooleanField from '@/components/Form/BooleanField'
import TextareaField from '@/components/Form/TextareaField'

import TextFieldIndex from '@/components/Form/Index/TextField'
import PhoneFieldIndex from '@/components/Form/Index/PhoneField'
import BooleanGroupFieldIndex from '@/components/Form/Index/BooleanGroupField'
import TextareaFieldIndex from '@/components/Form/Index/TextareaField'

import fieldValidator from '@/libraries/FieldValidator'
import Price from '@/components/Price'
import { checkout } from '@/stores/checkout'
import { termsUrl, termsTitle } from "../stores/Order";

import _forEach from 'lodash/forEach'
import {
  saveUserDetails,
  setUserDetailValue,
  userDetailValues,
} from '@/stores/UserDetails'

export default {
  components: {
    Alert,
    Banks,
    Button,
    TextField,
    PhoneField,
    BooleanGroupField,
    TextareaField,
    TextFieldIndex,
    PhoneFieldIndex,
    BooleanGroupFieldIndex,
    TextareaFieldIndex,
    Price,
    BooleanField
  },
  setup() {
    const loading = ref(false)

    const fields = reactive([
      {
        component: 'TextField',
        id: 'first_name',
        name: 'payment_first_name',
        label: __('form.first_name'),
        placeholder: '',
        value: userDetailValues.value['payment_first_name'] || '',
        rules: ['required'],
      },
      {
        component: 'TextField',
        id: 'last_name',
        name: 'payment_last_name',
        label: __('form.last_name'),
        placeholder: '',
        value: userDetailValues.value['payment_last_name'] || '',
        rules: ['required'],
      },
      {
        component: 'TextField',
        id: 'email',
        name: 'payment_email',
        label: __('form.email'),
        placeholder: '',
        value: userDetailValues.value['payment_email'] || '',
        rules: ['required', 'email'],
      },
      {
        component: 'PhoneField',
        id: 'phone',
        name: 'payment_phone',
        label: __('form.phone'),
        placeholder: '',
        value: userDetailValues.value['payment_phone'] || '',
        rules: ['required'],
      },
    ])

    const termsField = ref(null)

    if (termsUrl && termsTitle) {
      termsField.value = {
        url: termsUrl,
        title: termsTitle,
        confirmed: false,
        error: false
      }

      watch(termsField, value => {
        if(value.confirmed && value.error){
          value.error = false
        }
      }, {
        deep: true
      })
    }

    watch(fields, (fields) => {
      _forEach(fields, (field) => {
        if (field.value && field.error) {
          fieldValidator(field)
        }

        setUserDetailValue(field.name, field.value)
      })
    })

    const errorMessage = ref('')
    const nextStep = async () => {
      loading.value = true
      errorMessage.value = ''

      let anyErrors = false

      _forEach(fields, (field) => {
        const success = fieldValidator(field)
        if (!success) {
          anyErrors = true
        }
      })

      if(termsField.value){
        if(!termsField.value.confirmed){
          termsField.value.error = true
          anyErrors = true
        } else {
          termsField.value.error = false
        }
      }

      if (!anyErrors) {
        await saveUserDetails()
        const { success, message } = await checkout()
        if (!success && message) {
          errorMessage.value = message
          loading.value = false
        }
      } else {
        loading.value = false
      }
    }

    const cancel = async () => {
      setOverviewView()
    }

    return {
      loading,
      fields,
      cancel,
      nextStep,
      errorMessage,
      termsField
    }
  },
}
</script>

<template>
  <form @submit.prevent="nextStep">
    <div class="grid grid-cols-1 gap-10 lg:grid-cols-2">
      <div v-for="field in fields" :key="field.name">
        <component
          :is="field.component"
          :label="field.label"
          :placeholder="field.placeholder"
          :options="field.options || []"
          :vertical="field.vertical || false"
          :rules="field.rules"
          :error="field.error || false"
          :disabled="loading"
          v-model="field.value"
        />
      </div>
    </div>

    <div class="text-right mt-10" v-if="termsField">
      <BooleanField id="basket_terms" :label="termsField.title" :link="termsField.url" :rules="['required']" :error="termsField.error" :disabled="loading" v-model="termsField.confirmed" />
    </div>

    <Banks />

    <div class="mt-16">
      <Price />

      <Alert class="mt-12" styleType="danger" v-if="errorMessage">
        {{ errorMessage }}
      </Alert>

      <div class="flex justify-end flex-wrap -mx-6 -mb-6 mt-10">
        <div class="w-full p-6 md:w-1/2 lg:w-128">
          <Button
            variant="third"
            size="full"
            :disabled="loading"
            @click="cancel"
          >
            {{ __('btn_back') }}
          </Button>
        </div>

        <div class="w-full p-6 md:w-1/2 lg:w-128">
          <Button
            type="submit"
            variant="secondary"
            size="full"
            :loading="loading"
          >
            {{ __('btn_pay') }}
          </Button>
        </div>
      </div>
    </div>
  </form>
</template>
